import Phaser from 'phaser'
import type { PlacedTower, ActiveUnit, AssetManifest, TowerManifestEntry, UnitManifestEntry } from '@interfaces/index'
import { TEXTURE_KEYS } from '@interfaces/index'

/**
 * AssetRegistry — per-entity atlas lazy loader and frame-key resolver.
 *
 * Architecture:
 *  - One Phaser atlas per tower-id:  public/sprites/towers/<id>.{png,json}
 *  - One Phaser atlas per unit-id:   public/sprites/units/<id>.{png,json}
 *  - Manifest at public/sprites/manifest.json declares what exists.
 *  - Atlases are loaded lazily on first use so startup cost stays minimal.
 *  - All frame keys follow the binding naming convention:
 *      Tower: <id>_l<level>[path?]_<animation>_<frame>
 *      Unit:  <id>_<animation>_<dir>_<frame>  (or  <id>_<animation>_<frame>  for death)
 *  - Every public method has a procedural-fallback return value (null / false)
 *    so renderers never crash when assets are absent.
 *
 * Usage:
 *   1. Call AssetRegistry.init(scene, manifest?) in BootScene.create().
 *      Passing the manifest is optional; AssetRegistry can also load it lazily.
 *   2. In TowerRenderer.update():
 *        await AssetRegistry.ensureTowerLoaded(scene, tower.id)
 *        const frame = AssetRegistry.getTowerFrameKeyWithFallback(scene, tower, 'idle', frameIndex)
 *   3. In UnitRenderer.update():
 *        await AssetRegistry.ensureUnitLoaded(scene, unit.id)
 *        const frame = AssetRegistry.getUnitFrameKey(unit.id, 'walk', 'e', frameIndex)
 */
export class AssetRegistry {
  // ── Singleton scene reference (set via init()) ────────────────────────────
  private static _scene: Phaser.Scene | null = null

  // ── In-flight load promises keyed by atlas cache key ─────────────────────
  private static _loadingPromises: Map<string, Promise<void>> = new Map()

  // ── Loaded manifest (null until loaded) ──────────────────────────────────
  private static _manifest: AssetManifest | null = null

  // ── Manifest load promise (prevents parallel fetches) ────────────────────
  private static _manifestPromise: Promise<AssetManifest | null> | null = null

  // ─────────────────────────────────────────────────────────────────────────
  // Initialisation
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Initialise with the Phaser scene that owns the live texture manager.
   * Call once in BootScene.create() (before any renderer is constructed).
   * Optionally pass a pre-loaded manifest to skip the async fetch.
   */
  static init(scene: Phaser.Scene, manifest?: AssetManifest): void {
    AssetRegistry._scene = scene
    if (manifest !== undefined) {
      AssetRegistry._manifest = manifest
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Manifest
  // ─────────────────────────────────────────────────────────────────────────

  /** Load (and cache) the manifest from public/sprites/manifest.json. */
  static async loadManifest(): Promise<AssetManifest | null> {
    if (AssetRegistry._manifest !== null) return AssetRegistry._manifest
    if (AssetRegistry._manifestPromise !== null) return AssetRegistry._manifestPromise

    AssetRegistry._manifestPromise = fetch('sprites/manifest.json')
      .then(r => {
        if (!r.ok) {
          console.warn('[AssetRegistry] manifest.json not found — asset lazy-loading disabled')
          return null
        }
        return r.json() as Promise<AssetManifest>
      })
      .then(data => {
        AssetRegistry._manifest = data
        return data
      })
      .catch(err => {
        console.warn('[AssetRegistry] Failed to fetch manifest:', err)
        return null
      })

    return AssetRegistry._manifestPromise
  }

  /** Synchronous manifest access (null if not yet loaded). */
  static getManifest(): AssetManifest | null {
    return AssetRegistry._manifest
  }

  /** Returns tower entry from manifest, or undefined if not declared. */
  static getTowerEntry(towerId: string): TowerManifestEntry | undefined {
    return AssetRegistry._manifest?.towers[towerId]
  }

  /** Returns unit entry from manifest, or undefined if not declared. */
  static getUnitEntry(unitId: string): UnitManifestEntry | undefined {
    return AssetRegistry._manifest?.units[unitId]
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Tower Atlas Lazy Loading
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Ensures the per-entity tower atlas is loaded.
   * Safe to call every frame — returns immediately if already loaded.
   * Uses the provided scene (not the cached _scene) so it works even if
   * init() hasn't been called with a scene yet.
   */
  static async ensureTowerLoaded(scene: Phaser.Scene, towerId: string): Promise<void> {
    const key = TEXTURE_KEYS.TOWER_ATLAS(towerId)
    if (scene.textures.exists(key)) return

    const existing = AssetRegistry._loadingPromises.get(key)
    if (existing !== undefined) return existing

    const promise = new Promise<void>((resolve) => {
      scene.load.atlas(
        key,
        `sprites/towers/${towerId}.png`,
        `sprites/towers/${towerId}.json`,
      )
      const onComplete = (): void => { resolve() }
      const onError = (file: Phaser.Loader.File): void => {
        if (file.key === key) {
          console.debug(`[AssetRegistry] Tower atlas not found for '${towerId}' — using procedural fallback`)
          resolve()
        }
      }
      scene.load.once(Phaser.Loader.Events.COMPLETE, onComplete)
      scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError)
      scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
        scene.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError)
      })
      scene.load.start()
    })

    AssetRegistry._loadingPromises.set(key, promise)
    return promise
  }

  /**
   * Returns true if the tower atlas was successfully loaded.
   * Falls back to the cached _scene when no scene argument is given.
   */
  static hasTowerAtlas(towerId: string, scene?: Phaser.Scene): boolean {
    const s = scene ?? AssetRegistry._scene
    if (s === null) return false
    return s.textures.exists(TEXTURE_KEYS.TOWER_ATLAS(towerId))
  }

  /**
   * Legacy helper — kept for backwards compatibility with callers that used the
   * old single-image approach. Returns true if a tower atlas exists for this id.
   */
  static hasTowerSprite(towerId: string): boolean {
    return AssetRegistry.hasTowerAtlas(towerId)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Unit Atlas Lazy Loading
  // ─────────────────────────────────────────────────────────────────────────

  /** Ensures the per-entity unit atlas is loaded. Safe to call every frame. */
  static async ensureUnitLoaded(scene: Phaser.Scene, unitId: string): Promise<void> {
    const key = TEXTURE_KEYS.UNIT_ATLAS(unitId)
    if (scene.textures.exists(key)) return

    const existing = AssetRegistry._loadingPromises.get(key)
    if (existing !== undefined) return existing

    const promise = new Promise<void>((resolve) => {
      scene.load.atlas(
        key,
        `sprites/units/${unitId}.png`,
        `sprites/units/${unitId}.json`,
      )
      const onComplete = (): void => { resolve() }
      const onError = (file: Phaser.Loader.File): void => {
        if (file.key === key) {
          console.debug(`[AssetRegistry] Unit atlas not found for '${unitId}' — using procedural fallback`)
          resolve()
        }
      }
      scene.load.once(Phaser.Loader.Events.COMPLETE, onComplete)
      scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError)
      scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
        scene.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError)
      })
      scene.load.start()
    })

    AssetRegistry._loadingPromises.set(key, promise)
    return promise
  }

  /** Returns true if a per-entity unit atlas is loaded for the given unit id. */
  static hasUnitAtlas(unitId?: string, scene?: Phaser.Scene): boolean {
    const s = scene ?? AssetRegistry._scene
    if (s === null) return false
    if (unitId === undefined) return false
    return s.textures.exists(TEXTURE_KEYS.UNIT_ATLAS(unitId))
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Tower Frame Key Resolution
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Build the exact frame key for a tower state.
   * Does NOT verify the frame exists in the atlas.
   *
   * Level 1-2 → no path suffix:     archer_l1_idle_0
   * Level 3-5 → path suffix a or b: archer_l3a_attack_2, archer_l5b_idle_0
   */
  static getTowerFrameKey(
    tower: PlacedTower,
    animation: 'idle' | 'attack' | 'cast',
    frame: number,
  ): string {
    return TEXTURE_KEYS.TOWER_FRAME(tower.id, tower.level, tower.path, animation, frame)
  }

  /**
   * Returns the best available frame key for a tower, with progressive fallback:
   *   1. Exact level+path frame:      archer_l3a_idle_0
   *   2. Same level, path-less frame: archer_l3_idle_0   (artist skipped path split)
   *   3. L1 idle frame 0:             archer_l1_idle_0   (only base art exists)
   *   4. null if atlas is missing entirely → caller uses procedural rendering
   */
  static getTowerFrameKeyWithFallback(
    scene: Phaser.Scene,
    tower: PlacedTower,
    animation: 'idle' | 'attack' | 'cast',
    frame: number,
  ): string | null {
    const atlasKey = TEXTURE_KEYS.TOWER_ATLAS(tower.id)
    if (!scene.textures.exists(atlasKey)) return null

    const tex = scene.textures.get(atlasKey)

    // 1. Exact match
    const exact = AssetRegistry.getTowerFrameKey(tower, animation, frame)
    if (tex.has(exact)) return exact

    // 2. Same level, no path suffix (artist may have skipped path split)
    if (tower.level >= 3 && tower.path !== null) {
      const noPath = TEXTURE_KEYS.TOWER_FRAME(tower.id, tower.level, null, animation, frame)
      if (tex.has(noPath)) return noPath
    }

    // 3. Try frame 0 of the same animation at exact level
    if (frame !== 0) {
      const frame0Exact = AssetRegistry.getTowerFrameKey(tower, animation, 0)
      if (tex.has(frame0Exact)) return frame0Exact
    }

    // 4. Fall back to L1 idle frame 0 (minimum viable asset)
    const l1Idle0 = `${tower.id}_l1_idle_0`
    if (tex.has(l1Idle0)) return l1Idle0

    // 5. Atlas exists but no recognised frames → null for procedural fallback
    return null
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Unit Frame Key Resolution
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Build the exact frame key for a unit animation frame.
   * Direction-aware animations (walk/attack): goblin_walk_e_0
   * Direction-less animations (death):        goblin_death_3
   */
  static getUnitFrameKey(
    unitId: string,
    animation: string,
    dir: 'e' | 'w' | 'n' | 's' | null,
    frame: number,
  ): string {
    return TEXTURE_KEYS.UNIT_FRAME(unitId, animation, dir, frame)
  }

  /**
   * Returns the best available frame key for a unit, with fallback:
   *   1. Exact animation+dir+frame: goblin_walk_e_0
   *   2. Frame 0 of same animation: goblin_walk_e_0
   *   3. walk_e_0 (minimum viable)
   *   4. null → caller uses procedural rendering
   */
  static getUnitFrameKeyWithFallback(
    scene: Phaser.Scene,
    unit: ActiveUnit,
    animation: string,
    dir: 'e' | 'w' | 'n' | 's' | null,
    frame: number,
  ): string | null {
    const atlasKey = TEXTURE_KEYS.UNIT_ATLAS(unit.id)
    if (!scene.textures.exists(atlasKey)) return null

    const tex = scene.textures.get(atlasKey)

    // 1. Exact match
    const exact = AssetRegistry.getUnitFrameKey(unit.id, animation, dir, frame)
    if (tex.has(exact)) return exact

    // 2. Frame 0 of same animation+direction
    if (frame !== 0) {
      const frame0 = AssetRegistry.getUnitFrameKey(unit.id, animation, dir, 0)
      if (tex.has(frame0)) return frame0
    }

    // 3. Minimum viable: walk east frame 0
    const walkE0 = `${unit.id}_walk_e_0`
    if (tex.has(walkE0)) return walkE0

    return null
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Tile / Misc
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Returns true if the given tile texture key was successfully loaded.
   * Pass one of the TEXTURE_KEYS tile constants.
   */
  static hasTileSprite(key: string): boolean {
    if (AssetRegistry._scene === null) return false
    return AssetRegistry._scene.textures.exists(key)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Animation Cycle Helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Returns the current animation frame index based on wall-clock time.
   * @param frameCount  Total number of frames in the animation
   * @param fps         Desired animation speed in frames per second (default 4)
   */
  static animFrame(frameCount: number, fps = 4): number {
    return Math.floor(Date.now() / (1000 / fps)) % frameCount
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Clear all cached loading promises (call when scene is destroyed or restarted).
   * Does NOT unload textures from Phaser — those stay in the texture manager cache.
   */
  static clearPromiseCache(): void {
    AssetRegistry._loadingPromises.clear()
  }
}
